import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  Button,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { useLocalization } from '@/hooks/useLocalization';
import {
  skipViewingInvestorTagForAccountsQuery,
  useGetAccountsQuery,
} from '@/store/accountsApi';
import { useGetTabRecordsQuery } from '@/store/tabsApi';

interface SavedFilterEditorProps {
  excludedAccounts: number[];
  excludedCategories: number[];
  excludedAccountTypes: number[];
  onChange: (update: {
    excludedAccounts: number[];
    excludedCategories: number[];
    excludedAccountTypes: number[];
  }) => void;
}

const SavedFilterEditor: React.FC<SavedFilterEditorProps> = ({
  excludedAccounts,
  excludedCategories,
  excludedAccountTypes,
  onChange,
}) => {
  const { t } = useLocalization();
  const [activeTab, setActiveTab] = React.useState<string>('');

  const { data: tabRecords, isLoading: tabsLoading } =
    useGetTabRecordsQuery('investor-filters');

  const shouldLoadAccounts =
    activeTab === 'accounts' || (!tabRecords && !tabsLoading);
  const { data: accountsData, isLoading: accountsLoading } =
    useGetAccountsQuery(
      { showClosed: false, ...skipViewingInvestorTagForAccountsQuery },
      { skip: !shouldLoadAccounts },
    );
  const { data: allAccountsData, isLoading: allAccountsLoading } =
    useGetAccountsQuery({
      showClosed: false,
      ...skipViewingInvestorTagForAccountsQuery,
    });

  React.useEffect(() => {
    if (tabRecords && tabRecords.length > 0 && !activeTab) {
      setActiveTab(tabRecords[0].TabUIKey);
    }
  }, [tabRecords, activeTab]);

  const cashFlowCategories = React.useMemo(() => {
    if (!allAccountsData?.Records)
      return { assets: [], liabilities: [] } as {
        assets: { name: string; isAsset: boolean; classificationId: number }[];
        liabilities: {
          name: string;
          isAsset: boolean;
          classificationId: number;
        }[];
      };

    const cashFlowAccounts = allAccountsData.Records.filter(
      (account: {
        TransactionTypeName: string;
        ClassificationName: string;
        IsAsset: boolean;
        ClassificationId: number;
      }) => account.TransactionTypeName === 'Cash Flow',
    );

    const uniqueCategories = new Map<
      string,
      { name: string; isAsset: boolean; classificationId: number }
    >();

    cashFlowAccounts.forEach(
      (account: {
        ClassificationName: string;
        IsAsset: boolean;
        ClassificationId: number;
      }) => {
        if (!uniqueCategories.has(account.ClassificationName)) {
          uniqueCategories.set(account.ClassificationName, {
            name: account.ClassificationName,
            isAsset: account.IsAsset,
            classificationId: account.ClassificationId,
          });
        }
      },
    );

    const categories = Array.from(uniqueCategories.values());

    return {
      assets: categories
        .filter((cat) => cat.isAsset)
        .sort((a, b) => a.name.localeCompare(b.name)),
      liabilities: categories
        .filter((cat) => !cat.isAsset)
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [allAccountsData]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) =>
    setActiveTab(newValue);

  const setExcludedAccounts = (next: number[]) =>
    onChange({
      excludedAccounts: next,
      excludedCategories,
      excludedAccountTypes,
    });
  const setExcludedCategories = (next: number[]) =>
    onChange({
      excludedAccounts,
      excludedCategories: next,
      excludedAccountTypes,
    });
  const setExcludedAccountTypes = (next: number[]) =>
    onChange({
      excludedAccounts,
      excludedCategories,
      excludedAccountTypes: next,
    });

  const renderAccountsTab = () => {
    if (accountsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant='subtitle1'>
            <Checkbox
              indeterminate={
                excludedAccounts.length > 0 &&
                excludedAccounts.length < (accountsData?.Records?.length || 0)
              }
              checked={excludedAccounts.length === 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setExcludedAccounts([]);
                } else {
                  setExcludedAccounts(
                    accountsData?.Records?.map(
                      (a: { AccountId: number }) => a.AccountId,
                    ) || [],
                  );
                }
              }}
            />
            {t('nameAsc')}
          </Typography>
          <Typography variant='subtitle1'>{t('grid.balance')}</Typography>
        </Box>

        <Typography
          variant='subtitle2'
          sx={{ mb: 1, fontWeight: 600 }}
        >
          ▼ {t('open')}
        </Typography>

        {accountsData?.Records?.map(
          (account: {
            AccountId: number;
            Name: string;
            InstitutionName: string;
            Balance: number;
          }) => (
            <Box
              key={account.AccountId}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={!excludedAccounts.includes(account.AccountId)}
                  onChange={() => {
                    const next = excludedAccounts.includes(account.AccountId)
                      ? excludedAccounts.filter(
                          (id) => id !== account.AccountId,
                        )
                      : [...excludedAccounts, account.AccountId];
                    setExcludedAccounts(next);
                  }}
                />
                <Box>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 500 }}
                  >
                    {account.Name}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                  >
                    at {account.InstitutionName}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant='body2'
                sx={{ fontWeight: 500 }}
              >
                ${account.Balance?.toLocaleString() || '0.00'}
              </Typography>
            </Box>
          ),
        )}

        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button
            size='small'
            onClick={() => {
              setExcludedAccounts([]);
            }}
          >
            {t('clearAll')}
          </Button>
        </Box>
      </Paper>
    );
  };

  const renderCategoriesTab = () => {
    if (allAccountsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const totalCategories =
      cashFlowCategories.assets.length + cashFlowCategories.liabilities.length;
    const excludedCount = excludedCategories.length;
    const includedCount = totalCategories - excludedCount;

    if (totalCategories === 0) {
      return (
        <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              variant='h6'
              color='warning.main'
            >
              ⚠
            </Typography>
            <Typography
              variant='body1'
              sx={{ ml: 1, color: 'warning.main' }}
            >
              {t('noCashFlowCategoriesFound')}
            </Typography>
          </Box>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('noCashFlowAccountsFound')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant='subtitle1'>
            <Checkbox
              indeterminate={
                excludedCount > 0 && excludedCount < totalCategories
              }
              checked={excludedCount === 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setExcludedCategories([]);
                } else {
                  const allIds = [
                    ...cashFlowCategories.assets.map((c) => c.classificationId),
                    ...cashFlowCategories.liabilities.map(
                      (c) => c.classificationId,
                    ),
                  ];
                  setExcludedCategories(allIds);
                }
              }}
            />
            {t('categoriesSelected', { includedCount, totalCategories })}
          </Typography>
        </Box>

        {cashFlowCategories.assets.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant='subtitle1'
              sx={{ mb: 2, fontWeight: 600 }}
            >
              ▼ {t('assetsCount', { count: cashFlowCategories.assets.length })}
            </Typography>
            <Box sx={{ pl: 2 }}>
              {cashFlowCategories.assets.map((category) => (
                <Box
                  key={category.classificationId}
                  sx={{ display: 'flex', alignItems: 'center', py: 1 }}
                >
                  <Checkbox
                    checked={
                      !excludedCategories.includes(category.classificationId)
                    }
                    onChange={() => {
                      const next = excludedCategories.includes(
                        category.classificationId,
                      )
                        ? excludedCategories.filter(
                            (id) => id !== category.classificationId,
                          )
                        : [...excludedCategories, category.classificationId];
                      setExcludedCategories(next);
                    }}
                  />
                  <Typography variant='body2'>{category.name}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {cashFlowCategories.liabilities.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant='subtitle1'
              sx={{ mb: 2, fontWeight: 600 }}
            >
              ▼{' '}
              {t('liabilitiesCount', {
                count: cashFlowCategories.liabilities.length,
              })}
            </Typography>
            <Box sx={{ pl: 2 }}>
              {cashFlowCategories.liabilities.map((category) => (
                <Box
                  key={category.classificationId}
                  sx={{ display: 'flex', alignItems: 'center', py: 1 }}
                >
                  <Checkbox
                    checked={
                      !excludedCategories.includes(category.classificationId)
                    }
                    onChange={() => {
                      const next = excludedCategories.includes(
                        category.classificationId,
                      )
                        ? excludedCategories.filter(
                            (id) => id !== category.classificationId,
                          )
                        : [...excludedCategories, category.classificationId];
                      setExcludedCategories(next);
                    }}
                  />
                  <Typography variant='body2'>{category.name}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button
            size='small'
            onClick={() => {
              setExcludedCategories([]);
            }}
            disabled={excludedCount === 0}
          >
            {t('selectAll')}
          </Button>
        </Box>
      </Paper>
    );
  };

  const renderAccountTypesTab = () => {
    const accountTypes = [
      {
        code: 1,
        name: t('linkedAccounts'),
        description: t('linkedAccountsDescription'),
      },
      {
        code: 2,
        name: t('manuallyEnteredAccounts'),
        description: t('manuallyEnteredAccountsDescription'),
      },
      {
        code: 8,
        name: t('managedByAdvisor'),
        description: t('managedByAdvisorDescription'),
      },
    ];

    const totalAccountTypes = accountTypes.length;
    const excludedCount = excludedAccountTypes.length;
    const includedCount = totalAccountTypes - excludedCount;

    return (
      <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant='subtitle1'>
            <Checkbox
              indeterminate={
                excludedCount > 0 && excludedCount < totalAccountTypes
              }
              checked={excludedCount === 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setExcludedAccountTypes([]);
                } else {
                  setExcludedAccountTypes(accountTypes.map((t) => t.code));
                }
              }}
            />
            {t('accountTypesSelected', { includedCount, totalAccountTypes })}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          {accountTypes.map((accountType) => (
            <Box
              key={accountType.code}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Checkbox
                checked={!excludedAccountTypes.includes(accountType.code)}
                onChange={() => {
                  const next = excludedAccountTypes.includes(accountType.code)
                    ? excludedAccountTypes.filter(
                        (code) => code !== accountType.code,
                      )
                    : [...excludedAccountTypes, accountType.code];
                  setExcludedAccountTypes(next);
                }}
                sx={{ mt: 0.5 }}
              />
              <Box sx={{ flex: 1, ml: 1 }}>
                <Typography
                  variant='body1'
                  sx={{ fontWeight: 500 }}
                >
                  {accountType.name}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                >
                  {accountType.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button
            size='small'
            onClick={() => {
              setExcludedAccountTypes([]);
            }}
            disabled={excludedCount === 0}
          >
            {t('selectAll')}
          </Button>
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      {tabRecords && tabRecords.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label='filter tabs'
          >
            {tabRecords.map((tab) => (
              <Tab
                key={tab.TabUIKey}
                label={tab.Label}
                value={tab.TabUIKey}
                sx={{ textTransform: 'none' }}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {tabsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 'accounts' || !activeTab ? renderAccountsTab() : null}
          {activeTab === 'categories' ? renderCategoriesTab() : null}
          {activeTab === 'types' ? renderAccountTypesTab() : null}
          {activeTab === 'institutions' ? (
            <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
              <Typography variant='body1'>
                {t('institutionsContentComingSoon')}
              </Typography>
            </Paper>
          ) : null}
        </>
      )}
    </Box>
  );
};

export default SavedFilterEditor;
